import React from "react";

interface StatusBadgeProps {
  status: string;
}

function getClasses(status: string): string {
  // Null-safe: the API returns an absent refreshStatus for a dataset that has
  // never run, and calling .toLowerCase() on undefined threw during render,
  // which blanked the whole page before the error boundary existed.
  const s = (status ?? "").toLowerCase();

  if (s === "completed" || s === "success") {
    return "bg-success-subtle text-success-fg border-success-border";
  }
  // "failure" is what Microsoft Graph puts on a directory audit record, and
  // privilege changes store it verbatim. It used to fall through to neutral,
  // so a failed attempt to grant Global Administrator was painted the same
  // grey as a disabled schedule while the successes were painted green.
  if (s === "failed" || s === "failure" || s === "error") {
    return "bg-danger-subtle text-danger-fg border-danger-border";
  }
  if (s === "disabled") {
    return "bg-neutral-subtle text-neutral-fg border-neutral-border";
  }
  // Power BI reports a refresh that is still running as "Unknown" for dataset
  // history, but the dataflow transactions API says "InProgress" and the
  // collector stores that word verbatim — so both spellings really do reach
  // this function, from the two tabs of the same page.
  if (s === "unknown" || s === "inprogress" || s === "in progress" || s === "notstarted") {
    return "bg-info-subtle text-info-fg border-info-border";
  }
  return "bg-neutral-subtle text-neutral-fg border-neutral-border";
}

export default function StatusBadge({ status }: StatusBadgeProps) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${getClasses(status)}`}
    >
      {/* Not "Unknown": Power BI uses that word for a refresh that is still
          running, and this branch is an absent value. Two different states
          rendering the same word in two different colours is worse than a
          dash. */}
      {status || "—"}
    </span>
  );
}
